
import { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function LoginModal({ isOpen, onClose }: LoginModalProps) {
  const { loginWithSubscriberData } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<'form' | 'verification'>('form');
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [expiresAt, setExpiresAt] = useState<string>('');
  const [devCode, setDevCode] = useState<string>('');

  const handleClose = () => {
    setStep('form');
    setEmail('');
    setVerificationCode('');
    setError('');
    setSuccessMessage('');
    setExpiresAt('');
    setDevCode('');
    onClose();
  };

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsLoading(true);
    setError('');
    setDevCode('');

    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
      
      if (!supabaseUrl || !supabaseKey) {
        throw new Error('Supabase configuration is missing. Please check environment variables.');
      }
      
      const response = await fetch(`${supabaseUrl}/functions/v1/send-verification-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
        },
        body: JSON.stringify({
          email: email.trim(),
          type: 'login'
        }),
      });

      let data;
      try {
        const responseText = await response.text();
        data = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error('Invalid response from server');
      }

      if (!response.ok) {
        throw new Error(data.error || `Server error: ${response.status}`);
      }

      setExpiresAt(data.expires_at);
      
      if (data.dev_mode && data.test_code) {
        setDevCode(data.test_code);
        setSuccessMessage(`Development Mode: Your login code is ${data.test_code}. Also check your email and spam folder.`);
      } else {
        setSuccessMessage('Login code sent to your email! Please check your inbox and spam folder.');
      }
      
      setStep('verification');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send login code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!verificationCode.trim() || verificationCode.length !== 6) return;

    setIsLoading(true);
    setError('');

    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
      
      const response = await fetch(`${supabaseUrl}/functions/v1/verify-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
        },
        body: JSON.stringify({
          email: email.trim(),
          code: verificationCode.trim(),
          type: 'login'
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Invalid verification code');
      }

      // Login with subscriber data first
      loginWithSubscriberData(data.user);
      
      // Navigate to episodes immediately
      navigate('/episodes');
      
      // Add a longer delay before closing modal to ensure state propagation
      setTimeout(() => {
        handleClose();
      }, 300);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to verify code');
    } finally {
      setIsLoading(false);
    }
  };

  const formatExpiry = (expiryString: string) => {
    if (!expiryString) return '';
    const expiry = new Date(expiryString);
    return expiry.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        ></div>

        {/* Modal panel */}
        <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-6 pt-6 pb-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-800">
                {step === 'form' ? 'Welcome Back!' : 'Enter Login Code'}
              </h3>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            {step === 'form' && (
              <form onSubmit={handleSendCode} className="space-y-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-user-line text-amber-600 text-2xl"></i>
                  </div>
                  <p className="text-gray-600 text-sm">
                    Enter your email address to receive a secure login code
                  </p>
                </div>

                <div>
                  <label htmlFor="login-email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="login-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 text-sm"
                    placeholder="Enter your registered email"
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <i className="ri-error-warning-line text-red-500 text-xl mr-3"></i>
                      <p className="text-red-700 text-sm">{error}</p>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading || !email.trim()}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white font-bold py-3 px-6 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Sending Code...
                    </span>
                  ) : (
                    'Send Login Code'
                  )}
                </button>
              </form>
            )}

            {step === 'verification' && (
              <form onSubmit={handleVerifyCode} className="space-y-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-mail-check-line text-green-600 text-2xl"></i>
                  </div>
                  <h4 className="text-lg font-bold text-gray-800 mb-2">Check Your Email</h4>
                  <p className="text-gray-600 text-sm">
                    We've sent a 6-digit login code to<br />
                    <strong>{email}</strong>
                  </p>
                </div>

                {successMessage && (
                  <div className={`border rounded-lg p-4 ${devCode ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'}`}>
                    <div className="flex items-start">
                      <i className={`text-xl mr-3 mt-0.5 ${devCode ? 'ri-code-line text-blue-500' : 'ri-information-line text-green-500'}`}></i>
                      <div>
                        <p className={`text-sm ${devCode ? 'text-blue-700' : 'text-green-700'}`}>{successMessage}</p>
                        {expiresAt && (
                          <p className={`text-xs mt-2 ${devCode ? 'text-blue-600' : 'text-green-600'}`}>
                            Code expires at {formatExpiry(expiresAt)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label htmlFor="login-verification-code" className="block text-sm font-semibold text-gray-700 mb-2">
                    Login Code
                  </label>
                  <input
                    type="text"
                    id="login-verification-code"
                    value={verificationCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setVerificationCode(value);
                    }}
                    required
                    maxLength={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-5
                 00 focus:border-transparent transition-all duration-200 text-center text-2xl font-mono tracking-widest"
                    placeholder="000000"
                  />
                  <p className="text-sm text-gray-500 mt-2 text-center">
                    Enter the 6-digit code from your email{devCode && ' or use the code shown above'}
                  </p>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <i className="ri-error-warning-line text-red-500 text-xl mr-3"></i>
                      <p className="text-red-700 text-sm">{error}</p>
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  <button
                    type="submit"
                    disabled={isLoading || verificationCode.length !== 6}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white font-bold py-3 px-6 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center">
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Signing In...
                      </span>
                    ) : (
                      'Sign In'
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => setStep('form')}
                    className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-left-line mr-2"></i>
                    Back to Email
                  </button>
                </div>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleSendCode}
                    disabled={isLoading}
                    className="text-amber-600 hover:text-amber-700 text-sm font-medium transition-colors cursor-pointer"
                  >
                    Didn't receive the code? Resend
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
